import { formatDateForTable } from '../../../utils/formatDate';
import { Box, IconButton, Tooltip, Typography, Stack } from '@mui/material';
import TableActionPopup from '../../../components/Incidents/TableActionPopup';
import FirstArrivingUnit from '../../../components/Incidents/FirstArrivingUnit';
import {
  callStatusColorMatching,
  localNerisStatusMatching,
  statusMatching,
} from '../utils/statusMatching';
import { ReactComponent as NoteIcon } from '../../../assets/notes.svg';
import { ReactComponent as EditIcon } from '../../../assets/edit.svg';
import PrintIcon from '@mui/icons-material/Print';
import OfficerInChargeEdit from '../../../components/Incidents/OfficerInChargeEdit';
import { useDispatch } from 'react-redux';
import { changeIncidentEmployee } from '../../../store/incidentsReducer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const StatusBadge = ({ bg, color, text }) => (
  <Box
    sx={{
      background: bg,
      color: color,
      padding: '4px 12px',
      borderRadius: '99px',
      fontSize: '0.78rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      textAlign: 'center',
      lineHeight: 1.4,
    }}
  >
    {text}
  </Box>
);

export const useIncidentsColumns = ({
  openIncidentInfo,
  handlerSyncNeris,
  setOpenCreateNoteModal,
  permissions,
  employeesList,
  requestForIncidents,
  callStatus,
  setOpenPropertyDamageModal,
  handlePrintIncident,
}) => {
  const dispatch = useDispatch();

  const changeOfficerInChargeHandler = (incidentId, emloyeeId) => {
    dispatch(
      changeIncidentEmployee(incidentId, emloyeeId, requestForIncidents)
    );
  };
  return [
    {
      field: 'dispatch_time',
      headerName: 'Dispatch Time',
      flex: 1,
      minWidth: 130,
      resizable: true,
      renderCell: ({ value }) => {
        const date = formatDateForTable(value, true);
        return (
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a1a', lineHeight: 1.4 }}
            >
              {date.formattedDate}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>
              {date.formattedTime}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'incident_number',
      headerName: 'Number',
      flex: 0.7,
      minWidth: 105,
      resizable: true,
      renderCell: ({ value, row }) => (
        <Typography
          onClick={() => openIncidentInfo(row.incident_id)}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            color: '#1a73e8',
            fontSize: '0.82rem',
            fontWeight: 600,
            '&:hover': {
              textDecoration: 'underline',
              color: '#1558b0',
            },
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      field: 'incident_type',
      headerName: 'Type',
      flex: 1,
      minWidth: 110,
      maxWidth: 170,
      renderCell: ({ row }) => {
        let type;
        if (row.incident_type === null || row.incident_type === undefined || row.incident_type === '') {
          type = row.cad_type;
        } else {
          type = row.incident_type;
        }
        return (
          <Tooltip title={type || ''} arrow placement="top-start">
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 500,
                color: type ? '#333' : '#aaa',
                lineHeight: 1.3,
                width: '100%',
                // Wrap up to 2 lines, then ellipsis — fits inside the fixed 58px row height
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
              }}
            >
              {type || 'N/A'}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 2.1,
      minWidth: 150,
      renderCell: ({ value }) => (
        <Box
          sx={{
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            width: '100%',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          <Typography sx={{ fontSize: '0.82rem', color: '#333', display: 'inline' }}>
            {value || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 90,
      resizable: true,
      renderCell: ({ value }) => {
        const status = callStatusColorMatching(value?.toLowerCase());
        const statusValue = callStatus.find(
          (item) => item.value === value
        )?.label;
        return value ? (
          <Box
            sx={{
              background: status.bg,
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '0.78rem',
              fontWeight: 500,
            }}
          >
            {statusValue}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.78rem', color: '#aaa' }}>—</Typography>
        );
      },
    },
    {
      field: 'first_arriving_unit',
      headerName: 'Units',
      flex: 0.8,
      minWidth: 100,
      resizable: true,
      renderCell: ({ value, row }) => (
        <FirstArrivingUnit firstUnit={value} units={row.responded_units} />
      ),
    },
    {
      field: 'OfficerInCharge',
      headerName: 'OIC',
      flex: 1.2,
      minWidth: 160,
      resizable: true,
      renderCell: ({ row }) => (
        <OfficerInChargeEdit
          row={row}
          employeesList={employeesList}
          changeOfficerInChargeHandler={changeOfficerInChargeHandler}
          permissions={permissions}
        />
      ),
    },
    {
      field: 'form_status',
      headerName: 'Local',
      flex: 0.7,
      minWidth: 95,
      resizable: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => {
        const status = localNerisStatusMatching(value);
        return <StatusBadge bg={status.bg} color={status.color} text={status.text} />;
      },
    },
    {
      field: 'neris_status',
      headerName: 'USFA',
      flex: 0.7,
      minWidth: 95,
      resizable: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => {
        const status = statusMatching(value);
        return <StatusBadge bg={status.bg} color={status.color} text={status.text} />;
      },
    },
    {
      field: 'trt',
      headerName: 'TRT',
      flex: 0.6,
      minWidth: 80,
      resizable: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <Typography
          sx={{
            fontSize: '0.8rem',
            fontFamily: '"Roboto Mono", monospace',
            color: '#555',
          }}
        >
          {value ? new Date(value * 1000).toISOString().substr(11, 8) : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 150,
      resizable: true,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const { row } = params;
        const isHasNote = row.has_note;
        const isHasProperty = !!row.property_damage;
        const canEdit = permissions.can_edit || permissions.id === row.employee;

        return (
          <Stack direction="row" spacing={0} alignItems="center">
            <Tooltip title="Notes">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setOpenCreateNoteModal({ id: params.id, isHasNote })}
                  disabled={!permissions.can_add_notes}
                  sx={{
                    '& svg': { fill: isHasNote ? '#94e783' : 'none', width: 18, height: 18 },
                    '&:disabled svg path': { fill: 'none', stroke: '#00000042' },
                  }}
                >
                  <NoteIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Property Damage">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setOpenPropertyDamageModal({ id: params.id, isHasProperty })}
                  disabled={!permissions.can_add_notes}
                  sx={{
                    '& svg': { fill: isHasProperty ? '#62b651' : 'rgba(57,55,54,0.5)' },
                  }}
                >
                  <AttachMoneyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={() => openIncidentInfo(row.incident_id, true)}
                  sx={{
                    '& .menuItem_img': { width: 16, height: 16 },
                    '&:disabled .menuItem_img path': { stroke: '#00000042' },
                  }}
                >
                  <EditIcon className="menuItem_img" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Print">
              <IconButton
                size="small"
                onClick={() => handlePrintIncident(row.incident_id)}
              >
                <PrintIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <TableActionPopup
              params={params}
              openIncidentInfo={openIncidentInfo}
              handlerSyncNeris={handlerSyncNeris}
              permissions={permissions}
            />
          </Stack>
        );
      },
    },
  ];
};

export default useIncidentsColumns;
